<template>
  <Dialog :visible="manageDeploymentCredentialDialogVisible"
          @update:visible="handleClose">
    <template #header>
      {{ manageDeploymentCredentialId ? 'Edit' : 'Add' }} Deployment Credential
    </template>

    <div class="form-element">
      Name:
      <InputText v-model="manageDeploymentCredentialName" :invalid="manageDeploymentCredentialNameInvalid" class="form-element full-width" type="text"
                 @change="manageDeploymentCredentialNameChange"/>
      <div v-if="manageDeploymentCredentialNameInvalid">
        <InlineMessage severity="error">Credential Name is required.</InlineMessage>
      </div>
    </div>

    <div class="form-element">
      <div class="flex">
        Custom Authorization Header <InputSwitch class="ml" v-model="manageDeploymentCredentialAuthHeaderToggle"></InputSwitch>
      </div>
      <template v-if="manageDeploymentCredentialAuthHeaderToggle">
        <div class="form-element mt">
          <InputText v-model="manageDeploymentCredentialAuthHeader" class="form-element full-width" type="text"/>
        </div>
      </template>
    </div>

    <div class="form-element">
      <div class="flex">
        Anthropic Version <InputSwitch class="ml" v-model="manageDeploymentCredentialAnthropicVersionToggle"></InputSwitch>
      </div>
      <template v-if="manageDeploymentCredentialAnthropicVersionToggle">
          <div class="form-element mt">
            <InputText v-model="manageDeploymentCredentialAnthropicVersion" class="form-element full-width" type="text"/>
          </div>
      </template>
    </div>


    <Fieldset class="mt-2" @update:collapsed="updateCredentialsCollapsed" :legend="(manageDeploymentCredentialId? 'Input New ' : '') + 'Credential Value'"
              :toggleable="!!manageDeploymentCredentialId" :collapsed="!!manageDeploymentCredentialId">
      <div class="form-element">
        <InputText v-model="manageDeploymentCredentialValue" :invalid="manageDeploymentCredentialValueInvalid" class="form-element full-width" type="text"
                   @change="manageDeploymentCredentialValueChange"/>
        <div v-if="manageDeploymentCredentialValueInvalid">
          <InlineMessage severity="error">Credential Value is required.</InlineMessage>
        </div>
      </div>
    </Fieldset>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" type="button" @click="handleClose"></Button>
        <Button :disabled="manageDeploymentCredentialSubmitting" :loading="manageDeploymentCredentialSubmitting" label="Save" type="button" @click="saveDeploymentCredential"></Button>
      </div>
    </template>
  </Dialog>

  <div v-if="hasAdminAccess">
    <ProgressSpinnerContainer v-if="modelDeploymentsLoading" />
    <div v-else class="card">
      <div class="header-container mb flex">
        <h2 class="flex-1">AI Deployment Credentials</h2>
        <div class="align-right mr">
          <Button @click="manageDeploymentCredential(null)"><i class="pi pi-plus"></i> Add AI Deployment Credential</Button>
        </div>
      </div>
      <ScrollPanel class="scroll-panel" style="max-height: 60vh">
        <DataTable :value="deploymentCredentials">
          <Column field="name" header="Name"></Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button v-if="slotProps.data.platformAccountId > 0" severity="info" @click="manageDeploymentCredential(slotProps.data)" label="Edit"></Button>
              <Button v-if="slotProps.data.platformAccountId > 0" class="ml" severity="danger" @click="deleteDeploymentCredential(slotProps.data)" label="Delete"></Button>
            </template>
          </Column>
        </DataTable>
      </ScrollPanel>
    </div>
  </div>
  <div v-else>
    You do not have permission to access this page.
  </div>
</template>

<script setup>
import { useConfirm } from "primevue/useconfirm";
import ProgressSpinnerContainer from "../components/ProgressSpinnerContainer.vue";
import { useToast } from "primevue/usetoast";
import { ref, watch } from "vue";
import { useModelDeployments, useUsers } from "../composables/entities.js";
import { selectedAccount } from "../store/state.js";
import { deleteRequest, patch, post } from "../util/api.js";
import { decodeModelDeploymentCredential } from "../util/util.js";

const { modelDeploymentCredentials, modelDeploymentsLoading, deploymentCredentials } = useModelDeployments()

const { hasAdminAccess } = useUsers()

const toast = useToast()

const confirm = useConfirm()

async function deleteDeploymentCredential(data) {
  confirm.require({
    message: 'Are you sure you wish to delete ' + data.name + '?',
    header: 'Confirm Delete AI Deployment Credential',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        modelDeploymentsLoading.value = true
        const request = await deleteRequest('/crbn-tech/ai-model-deployment-credentials/' + data.id)
        if (request.id) {
          modelDeploymentCredentials.value = modelDeploymentCredentials.value.filter(md => String(md.id) !== String(request.id))
        }
        else {
          handleError()
        }
      }
      catch (e) {
        handleError(e)
      }
      finally {
        modelDeploymentsLoading.value = false
      }
      return true
    },
    reject: () => {
      return true
    }
  })
}

watch(modelDeploymentsLoading, (newValue)=> {
  if (!newValue) {
    if (!hasAdminAccess.value) {
      //router.replace('/')
    }
  }
}, { immediate: true })

const manageDeploymentCredentialDialogVisible = ref(false)
const manageDeploymentCredentialSubmitting = ref(false)

const manageDeploymentCredentialId = ref(null)
const manageDeploymentCredentialName = ref(null)
const manageDeploymentCredentialValue = ref(null)
const manageDeploymentCredentialMetadata = ref(null)
const manageDeploymentCredentialAuthHeader = ref(null)
const manageDeploymentCredentialAnthropicVersion = ref(null)

const manageDeploymentCredentialNameInvalid = ref(false)
const manageDeploymentCredentialValueInvalid = ref(false)


const manageDeploymentCredentialAuthHeaderToggle = ref(false)
const manageDeploymentCredentialAnthropicVersionToggle = ref(false)

function manageDeploymentCredentialNameChange() {
  manageDeploymentCredentialNameInvalid.value = !manageDeploymentCredentialName.value
}
function manageDeploymentCredentialValueChange() {
  manageDeploymentCredentialValueInvalid.value = !manageDeploymentCredentialName.value
}

function handleClose() {
  manageDeploymentCredentialId.value = null
  manageDeploymentCredentialName.value = null
  manageDeploymentCredentialValue.value = null
  manageDeploymentCredentialMetadata.value = null
  manageDeploymentCredentialAuthHeader.value = null
  manageDeploymentCredentialAnthropicVersion.value = null

  manageDeploymentCredentialNameInvalid.value = false
  manageDeploymentCredentialValueInvalid.value = false

  manageDeploymentCredentialDialogVisible.value = false
  manageDeploymentCredentialSubmitting.value = false
  manageDeploymentCredentialAuthHeaderToggle.value = false
  manageDeploymentCredentialAnthropicVersionToggle.value = false
}

function manageDeploymentCredential(data) {
  manageDeploymentCredentialDialogVisible.value = true
  manageDeploymentCredentialId.value = data?.id
  manageDeploymentCredentialName.value = data?.name
  manageDeploymentCredentialMetadata.value = data?.metadata
  manageDeploymentCredentialAuthHeader.value = data?.metadata?.authHeader
  manageDeploymentCredentialAnthropicVersion.value = data?.metadata?.anthropicVersion

  manageDeploymentCredentialAuthHeaderToggle.value = !!manageDeploymentCredentialAuthHeader.value
  manageDeploymentCredentialAnthropicVersionToggle.value = !!manageDeploymentCredentialAnthropicVersion.value
}

function handleError(e) {
  manageDeploymentCredentialSubmitting.value = false
  toast.add({
    severity: 'error',
    group: 'mainToast',
    life: 5000,
    detail: 'An error has occurred.  Please try again.'
  })
}
function handleCredentialError(e) {
  manageDeploymentCredentialSubmitting.value = false
  toast.add({
    severity: 'error',
    group: 'mainToast',
    life: 5000,
    detail: 'The credential metadata was updated, but the credential value could not be saved.  Please try again.'
  })
}

function resetValidations() {
  manageDeploymentCredentialNameInvalid.value = false
  manageDeploymentCredentialValueInvalid.value = false
}

async function saveDeploymentCredential() {
  let valid = true
  resetValidations()
  if (!manageDeploymentCredentialName.value) {
    manageDeploymentCredentialNameInvalid.value = true
    valid = false
  }
  if (!manageDeploymentCredentialValue.value) {
    if (!manageDeploymentCredentialId.value) {
      manageDeploymentCredentialValueInvalid.value = true
      valid = false
    }
  }

  if (!valid) {
    return false
  }

  const md = manageDeploymentCredentialMetadata.value ?? {}
  md.type = 'llm-model'
  if (manageDeploymentCredentialAuthHeaderToggle.value && manageDeploymentCredentialAuthHeader.value) {
    md.authHeader = manageDeploymentCredentialAuthHeader.value
  }
  else {
    delete md.authHeader
  }
  if (manageDeploymentCredentialAnthropicVersionToggle.value && manageDeploymentCredentialAnthropicVersion.value) {
    md.anthropicVersion = manageDeploymentCredentialAnthropicVersion.value
  }
  else {
    delete md.anthropicVersion
  }

  if (manageDeploymentCredentialId.value) {
    const payload = {
      title: manageDeploymentCredentialName.value,
      metadata: JSON.stringify(md)
    }
    manageDeploymentCredentialSubmitting.value = true
    try {
      let result = await patch('/crbn-tech/ai-model-deployment-credentials/' + manageDeploymentCredentialId.value, payload)
      if (result.id) {
        try {
          if (manageDeploymentCredentialValue.value && !credentialsCollapsed.value) {
            /* Only call the credentials endpoint if the credentials are entered. */
            const credentialPayload = {
              credential_value: manageDeploymentCredentialValue.value,
            }
            const credentialResult = await post('/crbn-tech/ai-model-deployment-credentials/' + result.id + '/credentials', credentialPayload)
            if (credentialResult.id) {
              result = credentialResult
            }
            else {
              handleError();
            }
          }
        }
        catch (e) {
          handleError(e)
        }
        for (let i = 0; i < modelDeploymentCredentials.value.length; i++) {
          if (String(modelDeploymentCredentials.value[i].id) === String(result.id)) {
            modelDeploymentCredentials.value[i] = decodeModelDeploymentCredential(result)
          }
        }
      }
      else {
        handleError();
      }
      handleClose()
    }
    catch (e) {
      manageDeploymentCredentialSubmitting.value = false
      handleError(e)
    }
  }
  else {
    const payload = {
      title: manageDeploymentCredentialName.value,
      cephisso_platform_accounts_id: selectedAccount.value,
      metadata: JSON.stringify(md)
    }
    manageDeploymentCredentialSubmitting.value = true
    try {
      let result = await post('/crbn-tech/ai-model-deployment-credentials', payload)
      if (result.id) {
        try {
          if (manageDeploymentCredentialValue.value) {
            /* Only call the credentials endpoint if the credentials are entered. */
            const credentialPayload = {
              credential_value: manageDeploymentCredentialValue.value,
            }
            const credentialResult = await post('/crbn-tech/ai-model-deployment-credentials/' + result.id + '/credentials', credentialPayload)
            if (credentialResult.id) {
              result = credentialResult
            }
            else {
              handleCredentialError();
            }
          }
        }
        catch (e) {
          handleError(e)
        }

        modelDeploymentCredentials.value.push(decodeModelDeploymentCredential(result))
      }
      else {
        handleError()
      }
      handleClose()
    }
    catch (e) {
      manageDeploymentCredentialSubmitting.value = false
      handleError(e)
    }
  }
}

const credentialsCollapsed = ref(false)
function updateCredentialsCollapsed(v) {
  credentialsCollapsed.value = v
}

</script>

<style scoped>
</style>